import type {FC} from 'react';
import {
  Box,
  DatePicker,
  Stack,
  TextField,
  Select,
} from '@wellstone-solutions/web';
import {Utils} from '@wellstone-solutions/common';
import type {FormType} from '@wellstone-solutions/common';
import type {Event} from 'api/v2';
import {FormLabel} from '@mui/material';
import {eventReminderIntervals} from '../../constants';
import {Recurrence} from 'modules/calendar/components/CalendarEvent/Form/Recurrence';
import {
  frequencySelectItems,
  RECURRENCE_FREQ_MAP,
} from 'modules/calendar/constants';

type PropsType = {
  form: FormType<Event>;
};

export const SchedulerForm: FC<PropsType> = ({form}) => {
  const handleStartTimeChange = (event) => {
    const startTime = event.target.value;

    if (startTime.length > 0) {
      const shiftedEndTime = Utils.shiftEndTime(
        {
          startTime: form.values.startTime,
          endTime: form.values.endTime,
        },
        startTime,
      );

      form.setFieldValue('startTime', startTime);
      form.setFieldValue('endTime', shiftedEndTime);
    } else {
      form.setFieldValue('startTime', event.target.value);
    }
  };

  return (
    <>
      <TextField
        label="Event title"
        formField="title"
        InputProps={{
          sx: {
            backgroundColor: '#fff',
            width: {
              xs: '100%',
              md: '75%',
            },
          },
        }}
        helperText="This is the meeting name that show up on calendars."
        onChange={form.handleFieldChange('title')}
        value={form.values.title}
        sx={{mb: 3}}
      />

      <DatePicker
        InputProps={{
          sx: {
            backgroundColor: '#fff',
            width: {
              xs: '100%',
              sm: '200px',
            },
            mb: 3,
          },
        }}
        data-testid="group-event-start-time"
        label="Start date"
        minDate={new Date()}
        onChange={(date) => {
          form.setFieldValue(
            'startDate',
            date?.isValid() ? date.toISOString() : '',
          );
        }}
        value={form.values.startDate}
      />

      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={3}>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={3}>
          <Box>
            <TextField
              type="time"
              fullWidth
              InputProps={{
                sx: {
                  backgroundColor: '#fff',
                  maxWidth: {
                    xs: '100%',
                    sm: 150,
                  },
                },
              }}
              data-testid="group-event-start-time"
              label="From"
              onChange={handleStartTimeChange}
              value={form.values.startTime}
            />
          </Box>
          <Box>
            <TextField
              formField="endTime"
              type="time"
              fullWidth
              InputProps={{
                sx: {
                  backgroundColor: '#fff',
                  maxWidth: {
                    xs: '100%',
                    sm: 150,
                  },
                },
              }}
              data-testid="group-event-end-time"
              label="To"
              onChange={form.handleFieldChange('endTime')}
              value={form.values.endTime}
            />
          </Box>
        </Stack>
        <Stack
          direction={{xs: 'column', md: 'row'}}
          alignItems={{xs: 'start', md: 'center'}}
          spacing={{
            xs: 0,
            md: 1,
          }}>
          <FormLabel sx={{color: 'darkest.main'}}>Reminder</FormLabel>
          <Select
            canUnselect={false}
            containerProps={{
              fullWidth: true,
              sx: {
                maxWidth: {
                  xs: '100%',
                  sm: 200,
                },
              },
            }}
            inputProps={{
              sx: {
                backgroundColor: '#fff',
              },
            }}
            value={form.values.reminderTime}
            onChange={(event) => {
              form.setFieldValue('reminderTime', event.target.value);
              form.setFieldTouched('reminderTime');
            }}
            data-testid="group-event-reminder"
            items={eventReminderIntervals}
          />
        </Stack>
      </Stack>

      <Box sx={{mt: 2}}>
        <Recurrence
          frequnecySelectItems={frequencySelectItems.filter(
            (item) => item.value !== RECURRENCE_FREQ_MAP.yearly,
          )}
          form={form}
          alwaysRecurring={true}
          isGroupEvent={true}
        />
      </Box>
    </>
  );
};
